import type { DirectoryData, EntityKind } from './types'

import { useQuery } from '@tanstack/react-query'
import { useLocale } from 'use-intl'

import { resolveImplementedLocale } from '~/lib/i18n/shared'
import { useCurrentRelativeLocation } from '~/lib/navigation/use-current-relative-location'

import { DirectoryPageView, PlayerProfileView, TeamProfileView } from './components'
import {
  directoryQueryOptions,
  playerProfileQueryOptions,
  teamProfileQueryOptions,
} from './query-options'
import { toEntityPageState } from './query-state'

export function DirectoryPage({ search }: {
  search: { q: string, type: EntityKind, page: number }
}) {
  const origin = useCurrentRelativeLocation()
  const locale = resolveImplementedLocale(useLocale())
  const validQuery = search.q.trim().length >= 2
  const query = useQuery({
    ...directoryQueryOptions(search),
    enabled: validQuery,
  })
  const idleData: DirectoryData = {
    query: search.q,
    type: search.type,
    items: [],
    pagination: { page: search.page, pageSize: 20, totalItems: 0, totalPages: 0, hasNextPage: false },
  }
  const state = validQuery ? toEntityPageState(query) : { status: 'success' as const, data: idleData }
  return <DirectoryPageView locale={locale} search={search} state={state} origin={origin} onRetry={() => void query.refetch()} />
}

export function TeamProfilePage({ teamId, playerPage = 1, back }: {
  teamId: string
  playerPage?: number
  back?: string
}) {
  const locale = resolveImplementedLocale(useLocale())
  const origin = useCurrentRelativeLocation()
  const query = useQuery(teamProfileQueryOptions(teamId, playerPage))
  return <TeamProfileView locale={locale} state={toEntityPageState(query)} back={back} origin={origin} onRetry={() => void query.refetch()} />
}

export function PlayerProfilePage({ playerId, appearancePage = 1, at, back }: {
  playerId: string
  appearancePage?: number
  at?: string
  back?: string
}) {
  const locale = resolveImplementedLocale(useLocale())
  const origin = useCurrentRelativeLocation()
  const query = useQuery(playerProfileQueryOptions(playerId, appearancePage, at))
  return <PlayerProfileView locale={locale} state={toEntityPageState(query)} back={back} origin={origin} onRetry={() => void query.refetch()} />
}
